import {
  FormLabel,
  Stack,
  TextField,
  Typography,
  FormControl,
  type SxProps,
} from '@mui/material';
import { useState } from 'react';
import type React from 'react';

import { HelpIcon } from '@/components/shared/helpIcon';

import type { FieldValidation } from './validation/fieldValidation';
import { InputErrorIcon } from './validation/inputErrorIcon';

export const FormTextField = ({
  size = 'medium',
  testId,
  title,
  helpText,
  alwaysShowHelp = false,
  dataTestId,
  validation,
  placeholder,
  value,
  onChange,
  sx,
  disabled = false,
}: {
  size?: 'small' | 'medium';
  testId?: string;
  title: React.ReactNode;
  helpText?: React.ReactNode;
  alwaysShowHelp?: boolean;
  dataTestId?: string;
  validation?: FieldValidation;
  placeholder: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps;
  disabled?: boolean;
}) => {
  const [touched, setTouched] = useState(false);
  const hasError =
    touched && (validation ? !validation.validateInput(value) : false);

  return (
    <FormControl className='group/help' sx={{ width: '100%', ...sx }}>
      {title && (
        <FormLabel>
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='subtitle1'>{title}</Typography>
            {helpText && (
              <HelpIcon
                title={helpText}
                alwaysVisible={alwaysShowHelp}
                className='text-(--mui-palette-text-secondary)'
              />
            )}
          </Stack>
        </FormLabel>
      )}
      <TextField
        size={size}
        data-testid={testId}
        onBlur={() => setTouched(true)}
        error={hasError}
        sx={{
          '& .MuiInputBase-root': {
            paddingRight: hasError ? '4px' : '24px',
          },
        }}
        slotProps={{
          htmlInput: { ...(dataTestId ? { 'data-testid': dataTestId } : {}) },
          input: {
            endAdornment: hasError && (
              <InputErrorIcon errorMessage={validation?.errorMessage(value)} />
            ),
          },
        }}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onChange={onChange}
      />
    </FormControl>
  );
};
